import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, ChevronDown, ChevronUp, Droplets, Loader2, MapPin } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FieldHealthChecks } from "@/components/field/FieldHealthChecks";

const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "pm" : "am";
  return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, "0")}${ampm}`;
};
const fmtDate = (d) =>
  new Date(`${d}T00:00:00`).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" });

const Chip = ({ tone, children, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold ${
    tone === "green" ? "bg-emerald-100 text-emerald-800"
    : tone === "amber" ? "bg-amber-100 text-amber-800"
    : "bg-slate-100 text-slate-600"}`}>
    {children}
  </span>
);

function AppointmentCard({ a, isAdmin, future, isTech, techs, onAssign, onReload }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const insp = a.inspection;
  const completed = insp?.status === "completed";
  const inProgress = insp?.status === "in_progress";
  const btnLabel = completed ? "VIEW INSPECTION" : inProgress ? "CONTINUE INSPECTION" : "START INSPECTION";
  // Appointments with an allocated partner only offer that partner's
  // techs; unallocated (partner_id null, admin-only view) offer all active.
  const partnerTechs = (techs || []).filter((t) => t.active && (!a.partner_id || t.partner_id === a.partner_id));
  return (
    <div data-testid={`field-appt-card-${a.id}`} className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs font-bold text-cyan-700">{fmtTime(a.start_time)}–{fmtTime(a.end_time)}</div>
          <div className="mt-0.5 font-bold text-slate-900 text-[15px] flex items-center gap-1.5">
            <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
            {a.street_address}, {a.suburb}
          </div>
          {isAdmin && a.partner_name && (
            <div className="mt-0.5 text-[11px] text-slate-400" data-testid={`field-appt-partner-${a.id}`}>
              Partner: {a.partner_name}
            </div>
          )}
        </div>
        {a.status === "cancelled" && <Chip>Cancelled</Chip>}
      </div>
      {(a.concerns || []).length > 0 && (
        <div className="mt-2 text-xs">
          <span className="text-slate-500">Customer concern:</span>{" "}
          <span className="font-semibold text-slate-800">{a.concerns.join(" · ")}</span>
        </div>
      )}
      {completed && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          <Chip tone="green" testid={`field-appt-inspection-chip-${a.id}`}>Inspection: Complete</Chip>
          {insp.sample_requested ? (
            insp.sample_collected ? (
              <Chip tone="green" testid={`field-appt-sample-chip-${a.id}`}>
                Sample: {insp.sample_source || "—"} — Collected
              </Chip>
            ) : (
              <Chip tone="amber" testid={`field-appt-sample-chip-${a.id}`}>Sample: Not collected</Chip>
            )
          ) : (
            <Chip testid={`field-appt-sample-chip-${a.id}`}>Sample: Declined</Chip>
          )}
          {a.booking && (
            <Chip tone={a.booking.paid ? "green" : "amber"} testid={`field-appt-payment-chip-${a.id}`}>
              Payment: {a.booking.paid ? "Paid" : "Pending"}
            </Chip>
          )}
        </div>
      )}
      {!isTech && (
        <div className="mt-3 flex items-center gap-2">
          <span className="text-[11px] font-bold text-slate-500 whitespace-nowrap">Assigned Technician</span>
          <select data-testid={`field-appt-assign-${a.id}`} value={a.assigned_technician_id || ""}
            onChange={(e) => onAssign(a.id, e.target.value || null)}
            className="flex-1 rounded-xl border border-slate-300 px-2 py-1.5 text-xs bg-white">
            <option value="">Unassigned</option>
            {partnerTechs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            {a.assigned_technician_id && !partnerTechs.some((t) => t.id === a.assigned_technician_id) && (
              <option value={a.assigned_technician_id}>{a.assigned_technician_name || "Assigned"}</option>
            )}
          </select>
        </div>
      )}
      {future ? (
        <>
          <button data-testid={`field-appt-details-btn-${a.id}`} onClick={() => setOpen(!open)}
            className="mt-3 w-full py-2.5 rounded-full border border-slate-300 text-slate-700 font-bold text-xs flex items-center justify-center gap-1">
            VIEW DETAILS {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {open && (
            <div className="mt-2 text-xs text-slate-600 space-y-1 bg-slate-50 rounded-xl p-3">
              <div><span className="text-slate-400">Customer:</span> <span className="font-semibold">{a.first_name || "—"}</span></div>
              <div><span className="text-slate-400">Campaign:</span> <span className="font-semibold">{a.campaign?.name || a.campaign?.code || "—"}</span></div>
              <div><span className="text-slate-400">Date:</span> <span className="font-semibold">{fmtDate(a.date)}</span></div>
            </div>
          )}
        </>
      ) : (
        <>
        <button data-testid={`field-appt-open-btn-${a.id}`}
          onClick={() => navigate(`/field/inspection/${a.id}`)}
          className={`mt-3 w-full py-3 rounded-full font-bold text-sm ${
            completed ? "border border-slate-300 text-slate-700 bg-white"
            : "bg-cyan-600 hover:bg-cyan-700 text-white"}`}>
          {btnLabel}
        </button>
        <button data-testid={`field-appt-quick-book-${a.id}`}
          onClick={() => navigate(`/field/quick-booking?appointment_id=${a.id}`)}
          className="mt-2 w-full py-2 rounded-full font-bold text-[12px] border border-cyan-300 text-cyan-700 bg-white hover:bg-cyan-50">
          Quick Test Booking
        </button>
        {(completed || inProgress) && (
          <button data-testid={`field-appt-clear-inspection-${a.id}`}
            onClick={async () => {
              if (!window.confirm("Clear this inspection so it can be run again from scratch?\n\nThe existing answers and photos are archived (recoverable by admin), and the job returns to Start Inspection.")) return;
              try {
                const { data: r } = await api.post(`/field/inspection/by-appointment/${a.id}/reset`);
                if (r.ok) { toast.success("Inspection cleared — ready to run again"); onReload?.(); }
                else toast.error(r.reason || "Nothing to clear");
              } catch (e) { toast.error(e.response?.data?.detail || "Could not clear inspection"); }
            }}
            className="mt-2 w-full py-2 rounded-full font-bold text-[12px] border border-rose-300 text-rose-700 bg-white hover:bg-rose-50">
            Clear Inspection (test again)
          </button>
        )}
        </>
      )}
    </div>
  );
}

export default function FieldToday() {
  const [data, setData] = useState(null);
  const [techs, setTechs] = useState([]);
  const [err, setErr] = useState("");
  const load = () => api.get("/field/appointments").then(({ data }) => {
    setData(data);
    if (data.role !== "technician") {
      api.get("/field/technicians").then(({ data: t }) => setTechs(t.technicians)).catch(() => {});
    }
  });
  useEffect(() => {
    load().catch((e) => setErr(e.response?.data?.detail || "Could not load appointments"));
  }, []); // eslint-disable-line

  const onAssign = async (aid, tid) => {
    try {
      await api.put(`/field/appointments/${aid}/assign`, { technician_id: tid });
      toast.success(tid ? "Technician assigned" : "Appointment unassigned");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Assignment failed"); }
  };

  const signOut = async () => {
    localStorage.removeItem("scwt_token");
    try { await api.post("/auth/logout"); } catch { /* cookie clear is best-effort */ }
    window.location.assign("/field/login");
  };

  if (err) return (
    <div className="min-h-screen bg-slate-50 px-4 py-16 text-center text-sm text-slate-500" data-testid="field-today-error">{err}</div>
  );
  if (!data) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
    </div>
  );

  const today = new Date().toLocaleDateString("en-CA");
  const appts = (data.appointments || []).filter((a) => a.status !== "cancelled");
  const todays = appts.filter((a) => a.date === today);
  const upcoming = appts.filter((a) => a.date > today);
  const recent = appts.filter((a) => a.date < today).slice(-10).reverse();
  const isAdmin = data.role === "admin";
  const isTech = data.role === "technician";

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 max-w-md mx-auto" data-testid="field-today-page">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Droplets className="w-6 h-6 text-cyan-600" />
          <div>
            <h1 className="text-xl font-bold text-slate-900">{isTech ? "My Water Health Checks" : "Today's Water Health Checks"}</h1>
            <div className="text-xs text-slate-400">{fmtDate(today)}{isAdmin ? " · All partners (admin)" : ""}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="field-quick-booking-btn"
            onClick={() => (window.location.href = "/field/quick-booking")}
            className="text-[11px] font-bold text-cyan-700 border border-cyan-300 bg-white hover:bg-cyan-50 rounded-full px-3 py-1.5"
          >
            Quick Test Booking
          </button>
          {isAdmin && (
            <button
              data-testid="field-practice-btn"
              onClick={async () => {
                try {
                  const { data: r } = await api.post("/admin/field-practice/reset");
                  toast.success(r.reset ? "Practice job reset — ready to rehearse again" : "Practice job created for today");
                  load();
                } catch (e) { toast.error(e.response?.data?.detail || "Could not create practice job"); }
              }}
              className="text-[11px] font-bold text-emerald-700 underline underline-offset-2 whitespace-nowrap"
            >
              Practice job
            </button>
          )}
          {!isTech && (
            <a href="/field/technicians" data-testid="field-technicians-link" className="text-[11px] font-bold text-cyan-700 underline underline-offset-2 whitespace-nowrap">Technicians</a>
          )}
          {isTech && (
            <button data-testid="field-sign-out" onClick={signOut} className="text-[11px] font-bold text-slate-500 underline underline-offset-2 whitespace-nowrap">Sign Out</button>
          )}
        </div>
      </div>

      <div className="mt-5 space-y-3" data-testid="field-today-list">
        {todays.length === 0 && (
          <div className="rounded-2xl bg-white border border-slate-200 p-6 text-center text-sm text-slate-400" data-testid="field-today-empty">
            {isTech ? "No Water Health Checks assigned." : "No Water Health Checks scheduled today."}
          </div>
        )}
        {todays.map((a) => <AppointmentCard key={a.id} a={a} isAdmin={isAdmin} isTech={isTech} techs={techs} onAssign={onAssign} onReload={load} />)}
      </div>

      {upcoming.length > 0 && (
        <>
          <h2 className="mt-8 text-sm font-bold text-slate-500 flex items-center gap-1.5">
            <CalendarDays className="w-4 h-4" /> Upcoming
          </h2>
          <div className="mt-3 space-y-3" data-testid="field-upcoming-list">
            {upcoming.map((a) => (
              <div key={a.id}>
                <div className="text-[11px] font-bold text-slate-400 mb-1">{fmtDate(a.date)}</div>
                <AppointmentCard a={a} isAdmin={isAdmin} isTech={isTech} techs={techs} onAssign={onAssign} onReload={load} future />
              </div>
            ))}
          </div>
        </>
      )}

      {recent.length > 0 && (
        <>
          <h2 className="mt-8 text-sm font-bold text-slate-500">Recent</h2>
          <div className="mt-3 space-y-3" data-testid="field-recent-list">
            {recent.map((a) => <AppointmentCard key={a.id} a={a} isAdmin={isAdmin} isTech={isTech} techs={techs} onAssign={onAssign} onReload={load} />)}
          </div>
        </>
      )}

      {["admin", "lab_technician", "technician"].includes(data.role) && (
        <FieldHealthChecks techs={techs} canAssign={!isTech} />
      )}
    </div>
  );
}
